import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useIsRedoAdmin } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  createContext,
  memo,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getFirstInstallRedirectPath,
  isWelcomeIndexPath,
} from "./first-install-redirect";
import { loadInitialFirstInstallStep } from "./load-first-install-step";

export const FirstInstallOnboardingPendingContext = createContext(false);

// Replaces the old full-screen onboarding modal: when first-install onboarding
// is incomplete, redirect to /welcome, which owns the whole flow.
export const FirstInstallRedirectGuard = memo(
  function FirstInstallRedirectGuard({ children }: { children: ReactNode }) {
    const team = useContext(TeamContext);
    const client = useRequiredContext(RedoMerchantClientContext);
    const isRedoAdmin = useIsRedoAdmin();
    const navigate = useNavigate();
    const location = useLocation();
    const locationRef = useRef(location.pathname);
    locationRef.current = location.pathname;
    const redirectedRef = useRef(false);

    const stepLoad = useLoad(async (signal) => {
      if (!team || isRedoAdmin || isWelcomeIndexPath(locationRef.current)) {
        return null;
      }
      const { step } = await loadInitialFirstInstallStep(client, signal);
      return step;
    }, []);

    useEffect(() => {
      if (redirectedRef.current || !team || stepLoad.pending) {
        return;
      }
      const path = getFirstInstallRedirectPath({
        step: stepLoad.value ?? null,
        isRedoAdmin,
        pathname: locationRef.current,
        teamId: String(team._id),
      });
      if (path) {
        redirectedRef.current = true;
        navigate(path, { replace: true });
      }
    }, [isRedoAdmin, navigate, stepLoad.pending, stepLoad.value, team]);

    const firstInstallOnboardingPending =
      stepLoad.pending || stepLoad.value != null;

    return (
      <FirstInstallOnboardingPendingContext.Provider
        value={firstInstallOnboardingPending}
      >
        {children}
      </FirstInstallOnboardingPendingContext.Provider>
    );
  },
);
